import { differenceInMonths } from 'date-fns'
import { parseISO } from 'date-fns'

import { unformattedNumber } from '~/libs/format'
import { flow, pickBy, get, intersection, reduce, some } from 'es-toolkit/compat'

export class MachineLearningService {
  constructor ($axios) {
    this.cache = {}
    this.$axios = $axios
  }

  cacheKey (key, value) {
    this.cache[key] = value
  }

  getCached (key) {
    return this.cache[key] || null
  }

  _prepAttributesForCategoryEndpoint (indexedAttributes) {
    const businessStartDate = get(indexedAttributes, 'business_start_date.value')
    const amountSeeking = get(indexedAttributes, 'financeAmount.value', null)
    const timeInBusiness = businessStartDate ? Math.abs(differenceInMonths(parseISO(businessStartDate), new Date())) : null
    const averageMonthlySales = get(indexedAttributes, 'average_monthly_sales.value', null)
    const creditScore = get(indexedAttributes, 'creditScore.value', null)

    if (some([amountSeeking, timeInBusiness, averageMonthlySales, creditScore].map(v => v === null))) {
      return null
    }

    return {
      creditScore: unformattedNumber(creditScore),
      timeInBusiness: unformattedNumber(timeInBusiness),
      'financeAmount': unformattedNumber(amountSeeking),
      'average_monthly_sales': unformattedNumber(averageMonthlySales)
    }
  }

  async fetchFundingCategoryPredictionFromBorrowerId (apiBaseUrl, borrowerId) {
    if (!borrowerId) {
      return null
    }

    const url = `${apiBaseUrl}/ml/loan-product/category/${borrowerId}`

    const { data } = await this.$axios.get(url)
    return data.data
  }

  fetchFundingCategoryPredictionFromAttributes (apiBaseUrl, attributes) {
    const data = this._prepAttributesForCategoryEndpoint(attributes)

    if (!data) {
      return null
    }

    return this.fetchFundingCategoryPrediction(apiBaseUrl, data)
  }

  async fetchFundingCategoryPrediction (apiBaseUrl, params) {
    const paramString = reduce(params, (result, value, key) => `${result}${key}=${value}&`, '?')
    const url = `${apiBaseUrl}/ml/loan-product/category${paramString}`

    const cached = this.getCached(paramString)
    if (cached) {
      return cached
    }

    const { data } = await this.$axios.get(url)

    this.cacheKey(paramString, data)

    return data
  }

  getCategoriesAboveThreshold (threshold, categories) {
    return flow(
      cats => pickBy(cats, v => v >= threshold),
      Object.keys
    )(categories)
  }

  filterAttributesByAffectedCategories (indexedAttributes, categories, ignoredAliases = []) {
    if (!indexedAttributes || !categories || !categories.length) {
      return indexedAttributes
    }

    return pickBy(indexedAttributes, attr => {
      // Missing affectedCategories annotation
      if (!attr.affectedCategories) {
        return true
      }

      // Part of the aliases we always keep
      if (ignoredAliases.includes(attr.alias)) {
        return true
      }

      return !!intersection(attr.affectedCategories, categories).length
    })
  }
}
